import { Typography, Stack, useTheme, Tooltip } from "@mui/material";
import { TasksResponse } from "@/modules/tasks/dtos";
import { isBefore, parseISO, isToday } from "date-fns";
import { useMemo } from "react";
import { TaskStatusesBackend } from "@/modules/tasks/enums";
import AssignedAgentDisplay from "@/modules/common/components/assigned-agent-display";
import {
  capitalizeFirstLetter,
  formatSmartDate,
  checkIfTaskIsOverdue,
} from "@/modules/tasks/utils";
import TaskCompletionCheckbox from "@/modules/tasks/components/task-completion-checkbox";
import { useMeetingCompletion } from "@/modules/tasks/hooks/use-meeting-completion.hook";

interface TaskItemProps {
  task: TasksResponse;
  isCompleting?: boolean;
  handleMarkComplete: (taskUid: string) => void;
  onClick?: () => void;
}

const TaskItem = ({
  task,
  isCompleting = false,
  handleMarkComplete,
  onClick,
}: TaskItemProps) => {
  const theme = useTheme();

  // Use the meeting completion hook to get utilities
  const { utilities } = useMeetingCompletion({});

  const isCompleted = task.status === TaskStatusesBackend.completed;

  const dueDate = useMemo(() => {
    if (!task.dueDate) return null;
    try {
      return parseISO(task.dueDate);
    } catch {
      return new Date(task.dueDate);
    }
  }, [task.dueDate]);

  const isPast = useMemo(() => {
    if (!dueDate) return false;
    return isBefore(dueDate, new Date()) && !isCompleted;
  }, [dueDate, isCompleted]);

  const isDueToday = useMemo(() => {
    if (!dueDate) return false;
    return isToday(dueDate);
  }, [dueDate]);

  const dateColor = useMemo(() => {
    if (isPast) {
      return theme.nawy.colors.semantic.text.extension.negative;
    } else if (isDueToday) {
      return theme.nawy.colors.semantic.text.tertiary;
    } else {
      return theme.nawy.colors.semantic.text.tertiary;
    }
  }, [isPast, isDueToday, theme]);

  const isDisabled = useMemo(() => {
    return (
      isCompleted ||
      (utilities.isMeetingTask(task) &&
        checkIfTaskIsOverdue(task.status, task.dueDate))
    );
  }, [isCompleted, utilities, task]);

  const handleToggleComplete = () => {
    if (!isDisabled && !isCompleting) {
      handleMarkComplete(task.uid);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={theme.nawy.spacing.xs}
      alignItems="start"
      alignSelf="stretch"
      padding={0}
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Tooltip
        title={
          utilities.isMeetingTask(task) &&
          checkIfTaskIsOverdue(task.status, task.dueDate)
            ? "This task is past due, so it can't be marked as done"
            : ""
        }
        disableInteractive={
          !utilities.isMeetingTask(task) ||
          !checkIfTaskIsOverdue(task.status, task.dueDate)
        }
        disableFocusListener={
          !utilities.isMeetingTask(task) ||
          !checkIfTaskIsOverdue(task.status, task.dueDate)
        }
        disableTouchListener={
          !utilities.isMeetingTask(task) ||
          !checkIfTaskIsOverdue(task.status, task.dueDate)
        }
        arrow
      >
        <span>
          <TaskCompletionCheckbox
            checked={isCompleted}
            onChange={handleToggleComplete}
            disabled={isDisabled}
            size={20}
            isLoading={isCompleting}
          />
        </span>
      </Tooltip>

      <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip
          title={capitalizeFirstLetter(task.title)}
          placement="top"
          arrow
        >
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {capitalizeFirstLetter(task.title)}
          </Typography>
        </Tooltip>

        {dueDate && (
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: dateColor,
            }}
          >
            {formatSmartDate(task.dueDate)}
          </Typography>
        )}

        {task.User && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={theme.nawy.spacing.xs}
            sx={{ mt: 0.5 }}
          >
            <AssignedAgentDisplay
              user={task.User as any}
              fontSize={theme.nawy.typography.english.variants.body.sm.semibold}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default TaskItem;
